// G2 : liste des inspections (missions de l'inspecteur, contrôle DGH), planification et modèles de formulaire,
// charte D-038. Ce qui demande une action en tête (rapports transmis à examiner, délais de correction
// dépassés, contrôles en cours), filtres appliqués côté serveur, tableau à lignes cliquables.

import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CODES_PROVINCES, formaterNombre } from '@carburanttrack/shared';
import { api } from '../../api/client';
import type { InstallationListe } from '../../api/types';
import { useFiltres } from '../../app/filtres';
import { aUnRole, useSession } from '../../app/session';
import { MessageErreur, Squelette } from '../../composants/Etats';
import { Icone } from '../../composants/Icones';
import { date, libelleCategorie, libelleProvince } from '../../composants/format';
import { classeEtatInspection, delaiDepasse, libelleDans, useConstatsASuivre, useListeInspections, useReferentielInspections } from './api';
import type { ModeleFormulaire } from './modele';
import './terrain.css';

const ROLES_PLANIFICATION = ['administrateur_national', 'directeur_general', 'directeur', 'chef_service', 'superviseur'];
const ROLES_TERRAIN = ['inspecteur', 'agent_terrain'];

const SEGMENTS: { etat: string; libelle: string }[] = [
  { etat: '', libelle: 'Toutes' },
  { etat: 'planifiee', libelle: 'Planifiées' },
  { etat: 'en_cours', libelle: 'En cours' },
  { etat: 'transmise', libelle: 'Transmises' },
  { etat: 'examinee', libelle: 'Examinées' },
  { etat: 'cloturee', libelle: 'Clôturées' },
];

export function ListeInspections() {
  const [params, setParams] = useSearchParams();
  const { province: provinceGlobale } = useFiltres();
  const { etat: session } = useSession();
  const referentiel = useReferentielInspections();
  const etat = params.get('etat') ?? '';
  const province = params.get('province') ?? provinceGlobale ?? '';
  const terrain = aUnRole(session, ...ROLES_TERRAIN);
  const mission = terrain && params.get('mission') !== 'toutes';
  const peutPlanifier = aUnRole(session, ...ROLES_PLANIFICATION);
  const filtres = new URLSearchParams();
  if (etat) filtres.set('etat', etat);
  if (province) filtres.set('province', province);
  if (mission) filtres.set('mission', 'oui');
  const requete = useListeInspections(filtres);
  const transmises = useListeInspections(new URLSearchParams({ etat: 'transmise' }), peutPlanifier);
  const constats = useConstatsASuivre(peutPlanifier);
  const changer = (cle: string, valeur: string) => {
    const p = new URLSearchParams(params);
    if (valeur) p.set(cle, valeur);
    else p.delete(cle);
    setParams(p, { replace: true });
  };

  const exceptions: { cle: string; ton: 'ambre' | 'rouge'; marque: string; titre: string; detail: string; lien: string }[] = [];
  const nTransmises = transmises.data?.length ?? 0;
  if (nTransmises > 0)
    exceptions.push({ cle: 'transmises', ton: 'ambre', marque: 'Examen, en attente', titre: `${formaterNombre(nTransmises)} rapport${nTransmises > 1 ? 's' : ''} transmis à examiner`, detail: 'L’examen rend le rapport et les constats visibles de l’organisation inspectée', lien: '/inspections?etat=transmise' });
  const enRetard = (constats.data ?? []).filter(delaiDepasse);
  if (enRetard.length > 0)
    exceptions.push({ cle: 'retard', ton: 'rouge', marque: 'Corrections, délai dépassé', titre: `${formaterNombre(enRetard.length)} constat${enRetard.length > 1 ? 's' : ''} dont le délai de correction est dépassé`, detail: 'Suivi à assurer avec l’organisation inspectée', lien: '/controle#constats' });
  if (terrain) {
    const enCours = (requete.data ?? []).filter((i) => i.etat === 'en_cours' && i.estDeMaMission);
    if (enCours.length > 0)
      exceptions.push({ cle: 'en-cours', ton: 'ambre', marque: 'Contrôles, en cours', titre: `${formaterNombre(enCours.length)} contrôle${enCours.length > 1 ? 's' : ''} à terminer`, detail: 'Saisie à compléter puis rapport à transmettre', lien: `/inspections/${enCours[0]!.id}` });
  }

  return (
    <div>
      <div className="page-tete">
        <div>
          <h1>Inspections</h1>
          <p>
            {requete.data ? `${formaterNombre(requete.data.length)} inspection${requete.data.length > 1 ? 's' : ''}, ` : ''}
            {province ? libelleProvince(province) : 'tout votre périmètre'}
            {mission ? ', vos missions' : ''}
          </p>
        </div>
        <div className="actions">
          <Link className="bouton" to="/inspections/formulaires">
            Modèles de formulaire
          </Link>
          {peutPlanifier && (
            <Link className="bouton primaire" to="/inspections/planifier">
              <Icone nom="ajout" />
              Planifier une inspection
            </Link>
          )}
        </div>
      </div>

      {exceptions.length > 0 && (
        <section className="a-traiter" aria-labelledby="titre-a-traiter">
          <h2 id="titre-a-traiter" className="sur-titre">
            Ce qui demande une action
          </h2>
          <ol className="liste-exceptions">
            {exceptions.map((e, i) => (
              <li key={e.cle}>
                <Link className={`exception ${e.ton}`} to={e.lien}>
                  <span className="numero" aria-hidden="true">
                    {i + 1}
                  </span>
                  <span className="texte">
                    <span className="marque-exception">{e.marque}</span>
                    <b>{e.titre}</b>
                    <small>{e.detail}</small>
                  </span>
                  <Icone nom="chevron" />
                </Link>
              </li>
            ))}
          </ol>
        </section>
      )}

      <div className="barre-filtres" role="search" aria-label="Filtres des inspections">
        <div className="segments" role="radiogroup" aria-label="État">
          {SEGMENTS.map((s) => (
            <button key={s.etat || 'toutes'} type="button" role="radio" aria-checked={etat === s.etat} onClick={() => changer('etat', s.etat)}>
              {s.libelle}
            </button>
          ))}
        </div>
        <div className="champ">
          <label htmlFor="insp-province">Province</label>
          <select id="insp-province" value={province} onChange={(e) => changer('province', e.target.value)}>
            <option value="">Toutes</option>
            {CODES_PROVINCES.map((c) => (
              <option key={c} value={c}>
                {libelleProvince(c)}
              </option>
            ))}
          </select>
        </div>
        {terrain && (
          <label className="case">
            <input type="checkbox" checked={mission} onChange={(e) => changer('mission', e.target.checked ? '' : 'toutes')} />
            <span>Mes missions seulement</span>
          </label>
        )}
      </div>

      {terrain && <p className="note">Vous ne voyez que les missions qui vous sont confiées dans votre périmètre (R-21). Les formulaires s’ouvrent aussi sans réseau depuis votre accueil.</p>}
      {requete.isLoading && <Squelette lignes={6} />}
      {requete.error && <MessageErreur erreur={requete.error} />}
      {requete.data && (
        <div className="tableau-conteneur">
          <table className="tableau lignes-48">
            <caption className="visuellement-cache">Inspections</caption>
            <thead>
              <tr>
                <th scope="col">Installation</th>
                <th scope="col">Référence</th>
                <th scope="col" className="masquable">
                  Organisation
                </th>
                <th scope="col">Date prévue</th>
                <th scope="col">État</th>
              </tr>
            </thead>
            <tbody>
              {requete.data.length === 0 && (
                <tr>
                  <td colSpan={5} className="attenue">
                    Aucune inspection ne correspond à ces critères dans votre périmètre.
                  </td>
                </tr>
              )}
              {requete.data.map((i) => (
                <tr key={i.id}>
                  <td>
                    <Link className="lien-ligne" to={`/inspections/${i.id}`}>
                      {i.installationNom}
                    </Link>
                    <small>
                      {libelleCategorie(i.installationCategorie)}, {libelleProvince(i.provinceCode)}
                    </small>
                  </td>
                  <td>
                    {i.reference}
                    <small>{i.objet}</small>
                  </td>
                  <td className="masquable">{i.organisationNom}</td>
                  <td>{date(i.datePrevue)}</td>
                  <td>
                    <span className={classeEtatInspection(i.etat)}>{libelleDans(referentiel.data?.etats, i.etat)}</span>
                    {i.estDeMaMission && !terrain && <small>votre mission</small>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Planification --------------------------------------------------------------------------------------

export function PlanifierInspection() {
  const naviguer = useNavigate();
  const referentiel = useReferentielInspections();
  const [q, setQ] = useState('');
  const [recherche, setRecherche] = useState('');
  const [installation, setInstallation] = useState<InstallationListe | null>(null);
  const [formulaireId, setFormulaireId] = useState('');
  const [nature, setNature] = useState('programmee');
  const [objet, setObjet] = useState('');
  const [datePrevue, setDatePrevue] = useState('');
  const [inspecteurs, setInspecteurs] = useState<string[]>([]);
  const installations = useQuery({
    queryKey: ['installations', 'planification', recherche],
    enabled: recherche.length >= 2,
    queryFn: async () => (await api<InstallationListe[]>(`/api/v1/installations?q=${encodeURIComponent(recherche)}&etat=valide&limite=20`)).donnees,
  });
  const possibles = useQuery({
    queryKey: ['inspections', 'inspecteurs', installation?.id],
    enabled: Boolean(installation),
    queryFn: async () => (await api<{ id: string; prenom: string; nom: string; courriel: string }[]>(`/api/v1/inspections/inspecteurs?installationId=${installation!.id}`)).donnees,
  });
  const formulaires = (referentiel.data?.formulaires ?? []).filter((f) => !installation || f.categories.includes(installation.categorie));
  const envoi = useMutation({
    mutationFn: async () => (await api<{ id: string }>('/api/v1/inspections', { corps: { installationId: installation!.id, formulaireId, nature, objet, datePrevue, inspecteurs } })).donnees,
    onSuccess: (d) => naviguer(`/inspections/${d.id}`),
  });
  const pret = Boolean(installation && formulaireId && objet.trim().length >= 3 && datePrevue && inspecteurs.length > 0);

  return (
    <div>
      <div className="page-tete">
        <div>
          <h1>Planifier une inspection</h1>
          <p>Seuls les inspecteurs dont le périmètre couvre l’installation sont proposés ; le serveur le vérifie de nouveau (R-21).</p>
        </div>
      </div>
      <form
        className="carte-bloc"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (pret) envoi.mutate();
        }}
      >
        <h2>1. Installation</h2>
        <div className="formulaire-ligne">
          <div className="champ">
            <label htmlFor="pl-q">Nom de l’installation (deux lettres au moins)</label>
            <input
              id="pl-q"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onBlur={() => setRecherche(q.trim())}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setRecherche(q.trim());
                }
              }}
            />
          </div>
        </div>
        {installations.isLoading && <Squelette lignes={2} />}
        {installations.data && installations.data.length === 0 && <p className="attenue">Aucune installation validée ne correspond dans votre périmètre.</p>}
        {installations.data && installations.data.length > 0 && (
          <ul className="choix-installation" aria-label="Installations trouvées">
            {installations.data.map((i) => (
              <li key={i.id}>
                <label>
                  <input
                    type="radio"
                    name="pl-installation"
                    checked={installation?.id === i.id}
                    onChange={() => {
                      setInstallation(i);
                      setInspecteurs([]);
                      setFormulaireId('');
                    }}
                  />
                  <span>
                    {i.nom}
                    <small>
                      {libelleCategorie(i.categorie)}, {i.organisationNom}, {libelleProvince(i.provinceCode)}
                    </small>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
        {installation && (
          <>
            <h2>2. Mission</h2>
            <div className="formulaire-ligne">
              <div className="champ">
                <label htmlFor="pl-formulaire">Modèle de formulaire</label>
                <select id="pl-formulaire" value={formulaireId} onChange={(e) => setFormulaireId(e.target.value)}>
                  <option value="">Choisir</option>
                  {formulaires.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.titre} (version {f.version})
                    </option>
                  ))}
                </select>
                {formulaires.length === 0 && <span className="aide">Aucun modèle en vigueur pour la catégorie « {libelleCategorie(installation.categorie)} ».</span>}
              </div>
              <div className="champ">
                <label htmlFor="pl-nature">Nature</label>
                <select id="pl-nature" value={nature} onChange={(e) => setNature(e.target.value)}>
                  {referentiel.data?.natures.map((n) => (
                    <option key={n.code} value={n.code}>
                      {n.libelle}
                    </option>
                  ))}
                </select>
              </div>
              <div className="champ">
                <label htmlFor="pl-date">Date prévue</label>
                <input id="pl-date" type="date" value={datePrevue} onChange={(e) => setDatePrevue(e.target.value)} />
              </div>
            </div>
            <div className="champ">
              <label htmlFor="pl-objet">Objet de la mission</label>
              <textarea id="pl-objet" rows={2} value={objet} onChange={(e) => setObjet(e.target.value)} />
            </div>
            <h2>3. Inspecteurs</h2>
            {possibles.isLoading && <Squelette lignes={2} />}
            {possibles.data?.length === 0 && <p className="message avertissement">Aucun inspecteur n’a d’affectation couvrant cette installation : la planification est impossible.</p>}
            <fieldset className="champ">
              <legend className="visuellement-cache">Inspecteurs de la mission</legend>
              {possibles.data?.map((u) => (
                <label key={u.id} className="case">
                  <input type="checkbox" checked={inspecteurs.includes(u.id)} onChange={(e) => setInspecteurs((l) => (e.target.checked ? [...l, u.id] : l.filter((x) => x !== u.id)))} />
                  <span>
                    {u.prenom} {u.nom}
                    <small>{u.courriel}</small>
                  </span>
                </label>
              ))}
            </fieldset>
          </>
        )}
        {envoi.error && <MessageErreur erreur={envoi.error} titre="Planification refusée" />}
        <div className="actions">
          <Link className="bouton discret" to="/inspections">
            Annuler
          </Link>
          <button type="submit" className="bouton primaire" disabled={!pret || envoi.isPending}>
            Planifier l’inspection
          </button>
        </div>
      </form>
    </div>
  );
}

// Modèles de formulaire -------------------------------------------------------------------------------

interface Formulaire {
  id: string;
  code: string;
  version: number;
  titre: string;
  description: string | null;
  categories: string[];
  modele: ModeleFormulaire;
  statut: string;
  motif: string | null;
  creeLe: string;
}

const LIBELLES_TYPES: Record<string, string> = {
  texte: 'Texte court',
  texte_long: 'Texte long',
  nombre: 'Nombre entier',
  decimal: 'Nombre décimal',
  booleen: 'Oui ou non',
  choix: 'Choix dans une liste',
  date: 'Date',
  photo: 'Photos',
};

export function ModelesFormulaire() {
  const requete = useQuery({ queryKey: ['inspections', 'formulaires'], queryFn: async () => (await api<Formulaire[]>('/api/v1/formulaires-inspection?tous=oui')).donnees });
  const [ouvert, setOuvert] = useState<string | null>(null);
  return (
    <div>
      <div className="page-tete">
        <div>
          <h1>Modèles de formulaire</h1>
          <p>Formulaires JSON versionnés par catégorie d’installation. Une nouvelle version archive la précédente sans modifier les inspections qui l’utilisent ; elle est publiée par l’administrateur national.</p>
        </div>
      </div>
      {requete.isLoading && <Squelette lignes={4} />}
      {requete.error && <MessageErreur erreur={requete.error} />}
      {requete.data?.map((f) => (
        <section key={f.id} className="carte-bloc" aria-labelledby={`titre-${f.id}`}>
          <div className="carte-bloc-tete">
            <h2 id={`titre-${f.id}`}>
              {f.titre} <small>version {f.version}</small>
            </h2>
            <span className={f.statut === 'actif' ? 'etiquette' : 'etiquette gris'}>{f.statut === 'actif' ? 'En vigueur' : 'Archivé'}</span>
          </div>
          {f.description && <p>{f.description}</p>}
          <p className="attenue">
            Catégories : {f.categories.map(libelleCategorie).join(', ')} ; {f.modele.sections.length} sections, {f.modele.sections.reduce((n, s) => n + s.champs.length, 0)} champs.
          </p>
          <button type="button" className="bouton" aria-expanded={ouvert === f.id} onClick={() => setOuvert(ouvert === f.id ? null : f.id)}>
            {ouvert === f.id ? 'Masquer la structure' : 'Afficher la structure'}
          </button>
          {ouvert === f.id &&
            f.modele.sections.map((s) => (
              <div key={s.code} className="espace-haut">
                <h3>
                  {s.titre}
                  {s.repetition !== 'aucune' && <small> répétée par {s.repetition === 'reservoirs' ? 'réservoir' : 'produit'}</small>}
                </h3>
                <div className="tableau-conteneur">
                  <table className="tableau">
                    <caption className="visuellement-cache">Champs de la section {s.titre}</caption>
                    <thead>
                      <tr>
                        <th scope="col">Champ</th>
                        <th scope="col">Type</th>
                        <th scope="col">Obligatoire</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.champs.map((c) => (
                        <tr key={c.code}>
                          <td>
                            {c.libelle}
                            {c.unite && <small>en {c.unite}</small>}
                          </td>
                          <td>
                            {LIBELLES_TYPES[c.type] ?? c.type}
                            {c.choix && <small>{c.choix.map((x) => x.libelle).join(', ')}</small>}
                          </td>
                          <td>{c.obligatoire ? 'Oui' : 'Non'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
        </section>
      ))}
    </div>
  );
}
